

















































































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {VueMaskDirective} from 'v-mask';
import BindAttrs from '@/utils/binds-attrs';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {isEqual} from 'lodash';
import {HTML_SERVICE_INSTANCE} from '@/services/html.service';
import moment from 'moment/moment';
import {ResizeSensor} from 'css-element-queries';
import {FieldStyleContext} from '@/fields/field-style-context';

type rulesFunction = (currentValue: string) => string | boolean;

interface TimePreset {
  label: string;
  calculateValue: () => string;
}
/**
 * General Component for Fields of type TIME
 *
 * https://confluence.ametras.com/x/L4RQAw
 *
 * attachRelatively:
 *  - true: attaches the dialog relatively to the component wrapper
 *          This is needed in case of a position:fixed surrounding panel (usually NOT)
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate},
  directives: {
    VueMaskDirective
  },
  mixins: [BindAttrs]
})
export default class AmetrasTimeFieldTemplate extends Vue {
  @Prop() textAlign!: 'LEFT' | 'RIGHT' | 'CENTER' | 'JUSTIFIED';
  @Prop({
    required: false,
    default: 'HH:mm'
  }) timeFormat!: string;
  @Prop({
    required: false,
    default: 'HH:mm'
  }) isoFormat!: string;
  @Prop({
    required: false
  }) value!: string;
  @Prop({
    required: false
  }) min!: string;
  @Prop({
    required: false
  }) max!: string;
  @Prop({
    required: false,
    default: false
  }) required!: boolean;
  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop() hideDefaultIcon!: boolean;
  /**
   * This is a flag indicating that the placeholder should not be cleared if readonly mode is active.
   */
  @Prop({
    default: true
  }) clearPlaceholderIfEmpty!: boolean;
  @Prop() readonly!: boolean;
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;
  /**
   * any external error present
   */
  @Prop() error!: boolean;
  @Prop() styleContext!: FieldStyleContext;
  @Prop() customRules!: [rulesFunction | string | boolean];
  showPicker = false;
  localeFormattedTextFieldValue = ''; // depending on the locale, e.g. (de) 22:30
  isoFormattedTimepickerValue = ''; // iso formatted timepicker value, e.g. 22:30
  mask = '';
  pickerFormat = 'HH:mm';
  listeners: Record<string, any> = {};
  presets: TimePreset[] = [{
    label: 'jetzt',
    calculateValue: () => {
      return moment().format(this.isoFormat);
    }
  }];
  offsetX: boolean = false;
  offsetY: boolean = true;

  dateRules = [(value: string) => {
    // validates the field value

    if (value && (this.forceShowErrors || !this.readonly)) {
      const striclyParsedValue = this.$moment(value, this.timeFormat, true);
      const isValidDate = striclyParsedValue.isValid();
      if (!isValidDate) {
        return 'ungültige Eingabe (' + this.timeFormat + ')';
      }
      if (this.min) {
        const striclyParsedMin = this.$moment(this.min, this.isoFormat, true);
        if (striclyParsedMin.isValid() && striclyParsedValue.isBefore(striclyParsedMin)) {
          return `Größer oder gleich ${striclyParsedMin.format(this.timeFormat)} erwartet`;
        }
      }
      if (this.max) {
        const striclyParsedMax = this.$moment(this.max, this.isoFormat, true);
        if (striclyParsedMax.isValid() && striclyParsedValue.isAfter(striclyParsedMax)) {
          return `Kleiner oder gleich ${striclyParsedMax.format(this.timeFormat)} erwartet`;
        }
      }
    } else if (!value && this.required && (this.forceShowErrors || !this.readonly)) {
      return 'Zeit fehlt';
    }
    const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
    return notHasAnyExternalError; // if any error from outside or any local error, return false
  }];

  $refs!: {
    inputfield: Vue
  };

  private originalInputListeners: any[] = [];
  private originalChangeListeners: any[] = [];

  private resizeListener: ResizeSensor | undefined;

  created() {
    if (this.value) {
      // if somebody provided a value from outside
      this.updateInternalModel(this.value);
    }
    // based on the current locale
    // this will create a mask, e.g. xx:xx (representing masked HH:mm)
    this.mask = this.timeFormat.replace(/[hms]/gi, '#');

    if (this.$listeners.input) {
      if (Array.isArray(this.$listeners.input)) {
        this.originalInputListeners = this.originalInputListeners.concat(this.$listeners.input);
      } else {
        this.originalInputListeners.push(this.$listeners.input);
      }
    }
    if (this.$listeners.change) {
      if (Array.isArray(this.$listeners.change)) {
        this.originalChangeListeners = this.originalChangeListeners.concat(this.$listeners.change);
      } else {
        this.originalChangeListeners.push(this.$listeners.change);
      }
    }
    this.listeners = {
      ...this.$listeners,
      input: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          // update model only if valid.
          for (const fnc of this.originalInputListeners) {
            fnc(valueToBePublished);
          }
        });
      },
      change: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          // update model only if valid.
          for (const fnc of this.originalChangeListeners) {
            fnc(valueToBePublished);
          }
        });
      }
    };
  }

  @Watch('value') modelChanges(newValue: string, oldValue: string) {
    if (!isEqual(newValue, oldValue)) {
      // if somebody provided a new value from outside
      this.updateInternalModel(newValue);
      this.refreshValidation();
    }
  }

  @Watch('required') requiredChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (newValue === false) {
        this.refreshValidation();
      }
    }
  }

  @Watch('showPicker') pickerVisible(newValue: string) {
    if (newValue) {
      this.resizeListener = new ResizeSensor(document.body, () => {
        this.calculatePickers();
      });
      this.calculatePickers();
      this.$emit('focus');
    } else {
      if (this.resizeListener) {
        this.resizeListener.detach();
      }
      this.$emit('blur');
    }
  }

  @Watch('isoFormattedTimepickerValue') onTimePicked(newValue: string, oldValue: string) {
    if (!isEqual(newValue, oldValue)) {
      // someone picked a value via picker
      const valueAsMoment = this.$moment(newValue, this.timeFormat, true);
      if (valueAsMoment.isValid()) {
        const formattedTime = valueAsMoment.format(this.isoFormat);
        if (!isEqual(formattedTime, this.value)) {
          this.$emit('change', formattedTime);
        }
        this.localeFormattedTextFieldValue = valueAsMoment.format(this.timeFormat);
        Vue.nextTick(() => {
          this.refreshValidation();
        });
      }
    }
  }

  @Watch('error') errorChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.refreshValidation();
    }
  }

  afterDatePicked() {
    this.showPicker = false;
    setTimeout(() => {
      // with minimum delay, focus the field again
      HTML_SERVICE_INSTANCE.focusFirstNode(this.$el);
    });
  }

  captureEscKey(event: Event) {
    if (this.showPicker) {
      // prevent blubbling of escape event if datepicker is shown
      event.stopPropagation();
      this.showPicker = false;
    }
  }

  activatePreset(preset: TimePreset) {
    this.updateInternalModel(preset.calculateValue());
  }

  private updateInternalModel(newValue: string) {
    const parsedMomentFromValue = this.$moment(newValue, this.isoFormat);
    if (parsedMomentFromValue.isValid()) {
      this.localeFormattedTextFieldValue = parsedMomentFromValue.format(this.timeFormat);
      this.isoFormattedTimepickerValue = newValue;
    } else {
      this.localeFormattedTextFieldValue = '';
      this.isoFormattedTimepickerValue = '';
    }
  }

  private applyModelValueIfValid(newValue: string, validCb: (valueToBePublished: string | null) => void) {
    const valueAsMoment = this.$moment(newValue, this.timeFormat, true);
    if (valueAsMoment.isValid()) {
      validCb(valueAsMoment.format(this.isoFormat));
    } else if (newValue === '') {
      validCb(newValue);
    }
  }

  private calculatePickers() {
    const inputFieldRect = this.$refs.inputfield.$el.getBoundingClientRect();

    // attention! if the height of the picker is changed, we must either calculate the height manually or modify these vals
    const menuHeight = 430;
    const buffer = 50;

    if (inputFieldRect.bottom + menuHeight + buffer > document.body.clientHeight) {
      // show at the right if there is not enough space at the bottom
      this.offsetX = true;
      this.offsetY = false;
    } else {
      // show at the bottom if there is enough space under the field
      this.offsetX = false;
      this.offsetY = true;
    }
  }

  private refreshValidation() {
    if (!this.simple) {
      const inputFieldRef = this.$refs.inputfield as any;
      if (inputFieldRef !== undefined) {
        inputFieldRef.validate();
      } else {
        throw new Error('Cannot refresh validation, the input field is not available');
      }
    }
  }

}
